// Aura Charm: a timed boost that doubles the Aura odds on the trainer's own hunt catches
// (1 in CONFIG.auraCatchOneIn becomes 1 in auraCatchOneIn / oddsMult). Eggs keep their own odds.
//
// Two clocks on the trainer's boosts: auraUntil for charms bought with diamonds ('paid') and
// auraFreeUntil for ones given free (admin grants, quests, events, the season track). Each one
// extends from where it stands; both running at once still means ×oddsMult, never more.
// A running charm is account state and can't be listed or traded; a bought or granted one is bound from the start.
// Ranked seasons pay Aura Charm items instead (shared/items.ts charmItem): tradeable until used from the Bag, when they
// add to the free clock (shared/catchcharm.ts useCharmItem).
//
// Every creature caught while a charm runs records which kind ran (CreatureDTO.boosted; 'paid'
// wins when both did), and keeps it through ascension, stars and trades, so a market can tell
// charm catches from plain ones.
import { CONFIG } from './config.ts';
import type { TrainerDTO } from './protocol.ts';

/** The diamond-shop offer id. */
export const AURA_CHARM = 'aura-charm';
export type CharmKind = 'paid' | 'free';
type Boosts = TrainerDTO['boosts'];

/** The charm running at `now`: 'paid' if a bought one runs (whatever else does), else 'free', else null. */
export function charmKind(b: Boosts | undefined, now: number): CharmKind | null {
  if (!b) return null;
  if ((b.auraUntil ?? 0) > now) return 'paid';
  if ((b.auraFreeUntil ?? 0) > now) return 'free';
  return null;
}

/** When the last charm running ends (0 when none runs). */
export function charmUntil(b: Boosts | undefined, now: number): number {
  const until = Math.max(b?.auraUntil ?? 0, b?.auraFreeUntil ?? 0);
  return until > now ? until : 0;
}

/** Aura odds (1 in n) of a hunt catch made at `now`. */
export function auraOneInAt(b: Boosts | undefined, now: number): number {
  return auraCatchOdds(!!charmKind(b, now));
}

/** Aura odds (1 in n) of a hunt catch, with or without a charm running. */
export function auraCatchOdds(charmed: boolean): number {
  return charmed ? CONFIG.auraCatchOneIn / CONFIG.auraCharm.oddsMult : CONFIG.auraCatchOneIn;
}

/** Add `ms` of charm of one kind, from the end of what already runs of that kind (or from now). */
export function extendCharm(b: Boosts, kind: CharmKind, ms: number, now: number): void {
  if (!(ms > 0)) return;
  if (kind === 'paid') b.auraUntil = Math.max(now, b.auraUntil ?? 0) + ms;
  else b.auraFreeUntil = Math.max(now, b.auraFreeUntil ?? 0) + ms;
}

/** The stronger of two kinds ('paid' beats 'free'). */
export function strongerKind(a: CharmKind | null | undefined, b: CharmKind | null | undefined): CharmKind | null {
  return a === 'paid' || b === 'paid' ? 'paid' : a ?? b ?? null;
}

/** One charm's length in ms. */
export const CHARM_MS = CONFIG.auraCharm.hours * 3_600_000;
